import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ReportDataSource} from './data/ReportDataSource';
import {ReportRow} from './data/reportRowModel';
import {Condition, ReportDefinitionV1Tasks} from './contracts/report-definition.types';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {defaultOperatorForDataType, operatorForValues} from './conditions/conditionOperators';
import {ReportTableState} from './ReportTable/ReportTable';
import {GroupingToken} from './ReportTable/useClientGrouping';
import {
	ACTIONS_COLUMN_ID,
	EXPANDER_COLUMN_ID,
	FAVORITE_COLUMN_ID,
	FIXED_COLUMN_IDS,
	flattenFieldPaths,
	orderedDataColumnIds,
} from './ReportTable/useReportTableColumns';
import {useViewerLayoutOverlay} from './useViewerLayoutOverlay';

export type BuilderMode = 'edit' | 'view';
export type BuilderRole = 'editor' | 'reader';

export interface ReportIntentPayload {
	reportType?: string;
	suggestedName?: string;
	definition: ReportDefinitionV1Tasks;
	summaries?: unknown[];
	flags?: unknown[];
	draftId?: string | null;
	// Original natural-language prompt the report was generated from. Rendered
	// (quoted, italic) in the AI banner per the design. Optional/forward-compatible:
	// the agent populates it on the create_report_intent event; when
	// absent the banner falls back to the static "Generated from your prompt".
	prompt?: string;
}

export interface ReportBuilderContextValue {
	definition: ReportDefinitionV1Tasks; // working draft
	prompt?: string; // original NL prompt (AI banner); undefined when the payload omits it
	mode: BuilderMode;
	role: BuilderRole;
	dirty: boolean;
	tableState: ReportTableState;
	grouping: GroupingToken[];
	taxonomy: ReportTaxonomy | null;
	setMode(m: BuilderMode): void;
	setName(name: string): void;
	setGrouping(g: GroupingToken[]): void;
	updateTableState(patch: Partial<ReportTableState>): void;
	save(): Promise<void>; // serialize layout/sort into definition, call dataSource.saveDefinition
	toggleFavorite(rowId: string): void; // updates row state in place (mock)
	addCondition(fieldKey: string): void; // no-op when taxonomy is null or fieldKey is unknown
	updateConditionValues(index: number, values: string[]): void;
	removeCondition(index: number): void;
	rows: ReportRow[];
	totalCount: number;
	capped: boolean;
	cappedTotalEstimate: number;
	totalIsFloor: boolean;
	// False until the first fetch for the current definition resolves. Lets the surface tell
	// "not fetched yet" apart from "fetched, zero matches" so the empty state never claims the
	// report ran while the initial fetch is still in flight.
	hasFetched: boolean;
	// True when the fetch rejected (non-2xx or network failure). Kept separate from `hasFetched`
	// so the surface can show an error instead of the empty state — the empty state must only ever
	// claim "the report ran and matched nothing" after a real success.
	fetchFailed: boolean;
	// Re-runs the fetch for the current definition. The fetch is otherwise only triggered by a
	// definition change, so without this a transient network failure would be a dead end.
	retryFetch(): void;
}

const ReportBuilderContext = createContext<ReportBuilderContextValue | null>(null);

/** Canonical `columnOrder` derived from the definition alone (no overlay/table-state input):
 * layout order when present, else field-tree order, wrapped by the fixed expander/favourite/actions
 * columns — reuses `useReportTableColumns`'s `orderedDataColumnIds` (Task 2) so the initial table
 * state matches what the columns hook will actually render (expander leading, favourite column
 * just before actions — see `useReportTableColumns`). */
function canonicalColumnOrder(definition: ReportDefinitionV1Tasks): string[] {
	return [EXPANDER_COLUMN_ID, ...orderedDataColumnIds(definition), FAVORITE_COLUMN_ID, ACTIONS_COLUMN_ID];
}

function canonicalColumnSizing(definition: ReportDefinitionV1Tasks): Record<string, number> {
	const sizing: Record<string, number> = {};
	for (const entry of definition.layout ?? []) {
		if (entry.width !== undefined) {
			sizing[entry.field] = entry.width;
		}
	}
	return sizing;
}

function canonicalSorting(definition: ReportDefinitionV1Tasks): ReportTableState['sorting'] {
	if (!definition.sort) {
		return [];
	}
	return [{id: definition.sort.field, desc: definition.sort.direction === 'DESC'}];
}

function canonicalTableState(definition: ReportDefinitionV1Tasks): ReportTableState {
	return {
		sorting: canonicalSorting(definition),
		columnOrder: canonicalColumnOrder(definition),
		columnSizing: canonicalColumnSizing(definition),
		columnVisibility: {},
	};
}

/** Serializes the current `tableState`/`grouping` into a NEW definition object (never mutates
 * `base`), per spec §6 save() rules: `layout` is `columnOrder` filtered of fixed (`__`-prefixed)
 * and hidden columns, paired with `columnSizing` when set; `sort` is the primary sort column, if
 * any; `grouping` is copied verbatim. */
function serializeDefinition(
	base: ReportDefinitionV1Tasks,
	tableState: ReportTableState,
	grouping: GroupingToken[]
): ReportDefinitionV1Tasks {
	const layout = tableState.columnOrder
		.filter(id => !id.startsWith('__'))
		.filter(id => tableState.columnVisibility[id] !== false)
		.map(id => {
			const width = tableState.columnSizing[id];
			return width !== undefined ? {field: id, width} : {field: id};
		});

	const primarySort = tableState.sorting[0];
	const sort = primarySort
		? {field: primarySort.id, direction: (primarySort.desc ? 'DESC' : 'ASC') as 'ASC' | 'DESC'}
		: undefined;

	return {
		...base,
		layout,
		sort,
		grouping: grouping.slice() as ReportDefinitionV1Tasks['grouping'],
	};
}

export interface ReportBuilderProviderProps {
	payload: ReportIntentPayload;
	role?: BuilderRole;
	// Required: callers pass the transport explicitly (production: HttpReportDataSource via
	// ReportBuilderPage; tests/Storybook: a mock). No implicit default, so a mock can never be the
	// silent production fallback.
	dataSource: ReportDataSource;
	// Scopes the per-user localStorage layout overlay to the logged-in person. The page passes the
	// viewer's person id from the app's auth context; falls back to 'me' only when the id is unknown
	// (e.g. tests/Storybook that don't supply one).
	userId?: string;
	// Field metadata driving addCondition/updateConditionValues; null until the taxonomy for this
	// report type has loaded. addCondition no-ops while null.
	taxonomy?: ReportTaxonomy | null;
	children: React.ReactNode;
}

/** Recursively flips `favorite` on the row matching `rowId` (and leaves every other row, including
 * siblings/children, referentially unchanged) so React only re-renders the affected row/ancestors. */
function toggleFavoriteInRows(rows: ReportRow[], rowId: string): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			changed = true;
			return {...row, favorite: !row.favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = toggleFavoriteInRows(row.children, rowId);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

export function ReportBuilderProvider({
	payload,
	role = 'editor',
	dataSource,
	userId = 'me',
	taxonomy = null,
	children,
}: ReportBuilderProviderProps): JSX.Element {
	const resolvedDataSource = dataSource;
	const reportId = payload.draftId ?? 'draft';

	const [definition, setDefinition] = useState<ReportDefinitionV1Tasks>(payload.definition);
	const [mode, setMode] = useState<BuilderMode>(role === 'reader' ? 'view' : 'edit');
	const [dirty, setDirty] = useState(false);
	const [grouping, setGroupingState] = useState<GroupingToken[]>((payload.definition.grouping ?? []) as GroupingToken[]);
	const [rows, setRows] = useState<ReportRow[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [capped, setCapped] = useState(false);
	const [cappedTotalEstimate, setCappedTotalEstimate] = useState(0);
	const [totalIsFloor, setTotalIsFloor] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [fetchFailed, setFetchFailed] = useState(false);
	// Bumped by `retryFetch` to re-run the fetch effect without changing the definition.
	const [fetchAttempt, setFetchAttempt] = useState(0);

	const validColumnIds = useMemo(() => {
		const ids = new Set(flattenFieldPaths(definition.fields));
		FIXED_COLUMN_IDS.forEach(id => ids.add(id));
		return ids;
	}, [definition.fields]);

	const {overlay, write: writeOverlay} = useViewerLayoutOverlay(reportId, userId, validColumnIds);

	const canonical = useMemo(() => canonicalTableState(definition), [definition]);

	// In-memory table state for the current session. Initialised from canonical + (for readers)
	// the persisted overlay on top; editors' in-memory edits flow through `save()` instead of the
	// overlay. Once a session has interacted with the table this local state is authoritative —
	// we intentionally do not re-derive it from `canonical`/`overlay` on every render (that would
	// stomp in-flight edits), only on definition identity change (see reset below).
	const initialTableState = useMemo<ReportTableState>(() => {
		if (role !== 'reader' || !overlay) {
			return canonical;
		}
		return {...canonical, ...overlay};
	}, [canonical]);

	const [tableState, setTableState] = useState<ReportTableState>(initialTableState);

	// Fetches on mount and whenever `definition` identity changes (e.g. after `save()`), plus on
	// demand via `retryFetch`. There is no loading spinner, so this stays fire-and-forget; a stale
	// in-flight response from a superseded definition is guarded against via the ref check.
	// `hasFetched` flips to true once the first response lands, so the empty state can wait for a
	// real (possibly zero-row) result rather than rendering during the initial in-flight window.
	// A rejection (non-2xx or network failure) is caught and surfaced as `fetchFailed` — leaving it
	// unhandled would both render a bare empty grid and raise an unhandled promise rejection.
	const latestDefinitionRef = useRef(definition);
	latestDefinitionRef.current = definition;

	useEffect(() => {
		let cancelled = false;
		const isStale = (): boolean => cancelled || latestDefinitionRef.current !== definition;
		setFetchFailed(false);
		void resolvedDataSource.fetchRows(definition, {offset: 0, limit: 1000}).then(
			result => {
				if (isStale()) {
					return;
				}
				setRows(result.rows);
				setTotalCount(result.totalCount);
				setCapped(result.capped);
				setCappedTotalEstimate(result.cappedTotalEstimate);
				setTotalIsFloor(result.totalIsFloor);
				setHasFetched(true);
			},
			() => {
				if (isStale()) {
					return;
				}
				// Clear any previous result so a stale table can't linger under the error.
				setRows([]);
				setTotalCount(0);
				setCapped(false);
				setCappedTotalEstimate(0);
				setTotalIsFloor(false);
				setHasFetched(false);
				setFetchFailed(true);
			}
		);
		return () => {
			cancelled = true;
		};
	}, [definition, resolvedDataSource, fetchAttempt]);

	const retryFetch = useCallback(() => {
		setFetchAttempt(n => n + 1);
	}, []);

	const setName = useCallback((name: string) => {
		setDefinition(prev => ({...prev, name}));
		setDirty(true);
	}, []);

	const setGrouping = useCallback((g: GroupingToken[]) => {
		setGroupingState(g);
		setDirty(true);
	}, []);

	const updateTableState = useCallback(
		(patch: Partial<ReportTableState>) => {
			setTableState(prev => ({...prev, ...patch}));
			if (role === 'reader') {
				writeOverlay(patch);
			} else {
				setDirty(true);
			}
		},
		[role, writeOverlay]
	);

	const save = useCallback(async () => {
		const nextDefinition = serializeDefinition(definition, tableState, grouping);
		await resolvedDataSource.saveDefinition(nextDefinition);
		setDefinition(nextDefinition);
		setDirty(false);
	}, [definition, tableState, grouping, resolvedDataSource]);

	const toggleFavorite = useCallback((rowId: string) => {
		setRows(prev => toggleFavoriteInRows(prev, rowId));
	}, []);

	const addCondition = useCallback(
		(fieldKey: string) => {
			if (!taxonomy) {
				return;
			}
			const field = fieldByKey(taxonomy, fieldKey);
			// Skip a field with no `filterField`: a condition stores the filterField enum, so a null
			// one would create a `field: null` condition that crashes label/summary rendering. Such
			// fields (non-filterable derived booleans) are never offered by the editor, but guard here
			// too so no caller can inject one.
			if (!field || !field.filterField) {
				return;
			}
			setDefinition(prev => ({
				...prev,
				filter: {
					...prev.filter,
					filterConditions: [
						...prev.filter.filterConditions,
						{
							// Conditions store the filterField enum (STATUS, PROJECT, ...), not the
							// taxonomy key the picker is keyed by; the editor passes the key, we store
							// the field's filterField so it round-trips through the report-service contract.
							field: field.filterField,
							operator: defaultOperatorForDataType() as Condition['operator'],
							values: [],
							context: field.context as Condition['context'],
						},
					],
				},
			}));
			setDirty(true);
		},
		[taxonomy]
	);

	const updateConditionValues = useCallback((index: number, values: string[]) => {
		setDefinition(prev => {
			const filterConditions = prev.filter.filterConditions.map((condition, i) =>
				i === index
					? {...condition, values, operator: operatorForValues(condition.operator, values) as Condition['operator']}
					: condition
			);
			return {...prev, filter: {...prev.filter, filterConditions}};
		});
		setDirty(true);
	}, []);

	const removeCondition = useCallback((index: number) => {
		setDefinition(prev => ({
			...prev,
			filter: {
				...prev.filter,
				filterConditions: prev.filter.filterConditions.filter((_, i) => i !== index),
			},
		}));
		setDirty(true);
	}, []);

	const value: ReportBuilderContextValue = {
		definition,
		prompt: payload.prompt,
		mode,
		role,
		dirty,
		tableState,
		grouping,
		taxonomy,
		setMode,
		setName,
		setGrouping,
		updateTableState,
		save,
		toggleFavorite,
		addCondition,
		updateConditionValues,
		removeCondition,
		rows,
		totalCount,
		capped,
		cappedTotalEstimate,
		totalIsFloor,
		hasFetched,
		fetchFailed,
		retryFetch,
	};

	return <ReportBuilderContext.Provider value={value}>{children}</ReportBuilderContext.Provider>;
}

export function useReportBuilder(): ReportBuilderContextValue {
	const ctx = useContext(ReportBuilderContext);
	if (!ctx) {
		throw new Error('useReportBuilder must be used within a ReportBuilderProvider');
	}
	return ctx;
}
